import { MEMBER_ROLES_VALUES, schema } from '@edgevanta/db/drizzle/client';
import { createInsertSchema } from 'drizzle-zod';
import { z } from 'zod/v4';

export const insertMemberSchema = createInsertSchema(schema.members, {
  role: z.enum(MEMBER_ROLES_VALUES),
});

const optionalName = z.string().trim().optional();

export const addMemberSchema = z.object({
  email: z.email(),
  role: z.enum(MEMBER_ROLES_VALUES),
  firstName: optionalName,
  lastName: optionalName,
});

export const selectMemberSchema = z.object({
  id: z.string(),
  name: z.string(),
  firstName: z.string(),
  lastName: z.string().nullable(),
  email: z.string(),
  role: z.enum(MEMBER_ROLES_VALUES),
  userId: z.string(),
});

export const selectMembersByUserIdSchema = z.object({
  id: z.string(),
  userId: z.string(),
  organizationId: z.string(),
  organizationName: z.string(),
  role: z.enum(MEMBER_ROLES_VALUES),
  createdAt: z.date(),
});

export const selectMembersByOrganizationIdSchema = z.object({
  id: z.string(),
  userId: z.string(),
  organizationId: z.string(),
  userFirstName: z.string().nullable(),
  userLastName: z.string().nullable(),
  userEmail: z.string(),
  role: z.enum(MEMBER_ROLES_VALUES),
  createdAt: z.date(),
});

export const getMembersByUserIdQuerySchema = z.object({
  userId: z.string(),
  limit: z.coerce.number().optional().default(25),
  offset: z.coerce.number().optional().default(0),
  organizationName: z.string().optional(),
  role: z
    .string()
    .optional()
    .transform((val) => {
      if (!val) {
        return [];
      }
      return val.split(',').map((role) => role);
    })
    .default([]),
  createdAt: z
    .string()
    .optional()
    .transform((val) => {
      if (!val) {
        return [];
      }

      return val.split(',').map((num) => Number(num));
    })
    .default([]),
  sort: z
    .string()
    .optional()
    .transform((val) => {
      if (!val) {
        return [
          {
            id: 'organizationName',
            desc: false,
          },
        ];
      }

      try {
        const parsed = JSON.parse(val);
        const sortSchema = z.array(
          z.object({
            id: z.string(),
            desc: z.boolean(),
          }),
        );

        const result = sortSchema.safeParse(parsed);

        if (!result.success) {
          return [
            {
              id: 'organizationName',
              desc: false,
            },
          ];
        }

        return result.data;
      } catch {
        return [
          {
            id: 'organizationName',
            desc: false,
          },
        ];
      }
    })
    .default([
      {
        id: 'organizationName',
        desc: false,
      },
    ]),
});

export const getMembersByOrganizationIdQuerySchema = z.object({
  organizationId: z.string(),
  limit: z.coerce.number().optional().default(25),
  offset: z.coerce.number().optional().default(0),
  userEmail: z.string().optional(),
  showOwner: z.boolean().optional().default(false),
  showInternal: z.boolean().optional().default(false),
  role: z
    .string()
    .optional()
    .transform((val) => {
      if (!val) {
        return [];
      }

      return val.split(',').map((role) => role);
    })
    .default([]),
  createdAt: z
    .string()
    .optional()
    .transform((val) => {
      if (!val) {
        return [];
      }

      return val.split(',').map((num) => Number(num));
    })
    .default([]),
  sort: z
    .string()
    .optional()
    .transform((val) => {
      if (!val) {
        return [
          {
            id: 'userEmail',
            desc: false,
          },
        ];
      }

      try {
        const parsed = JSON.parse(val);
        const sortSchema = z.array(
          z.object({
            id: z.string(),
            desc: z.boolean(),
          }),
        );

        const result = sortSchema.safeParse(parsed);

        if (!result.success) {
          return [
            {
              id: 'userEmail',
              desc: false,
            },
          ];
        }

        return result.data;
      } catch {
        return [
          {
            id: 'userEmail',
            desc: false,
          },
        ];
      }
    })
    .default([
      {
        id: 'userEmail',
        desc: false,
      },
    ]),
});

// `role` is the only updatable member column, so the update boundary is exactly
// `{ id, role }`. Deriving from `insertMemberSchema.partial()` would also admit
// `createdAt`/`updatedAt` and leak them into `UpdateMember`; picking keeps the
// schema as narrow as the actual API. Requiring `role` also means a payload with
// no role is a 400 here rather than reaching Drizzle's `.set({})`, which throws
// "No values to set" (EDG-1326).
//
// This schema stays role-only ON PURPOSE: it gates BOTH the tenant and the
// operations member-update actions. Names live on the shared `users` row, so
// accepting them here would let a tenant org admin rename a user across every
// org they belong to. Name editing is Operations-only — see
// `operationsUpdateMemberSchema`. Do not add `firstName`/`lastName` here.
export const updateMemberSchema = insertMemberSchema
  .pick({
    role: true,
  })
  .extend({
    id: z.string(),
  });

// Operations-only extension: platform staff (`requireUserAdmin`) may rename the
// underlying user while updating a member. Bound solely by the operations
// action; the tenant action stays on `updateMemberSchema` above so the
// cross-org rename cannot be expressed from a tenant request.
export const operationsUpdateMemberSchema = updateMemberSchema.extend({
  firstName: z.string().optional(),
  lastName: z.string().optional(),
});

export const getMembersQuerySchema = z
  .object({
    search: z.string().optional(),
  })
  .merge(
    z.object({
      limit: z.coerce.number().optional().default(25),
      offset: z.coerce.number().optional().default(0),
      sortBy: z.enum(['createdAt', 'name']).optional().default('createdAt'),
      page: z.coerce.number().optional(),
    }),
  );

export const deleteMemberSchema = z.object({
  id: z.string(),
});
